import { Image } from "react-konva";
import { useMemo } from "react";

type TransferImageProps = {
  image: HTMLImageElement;
  canvasWidth: number;
  canvasHeight: number;
  labType?: "design" | "transfers";
};

export function TransferImage({ image, canvasWidth, canvasHeight, labType }: TransferImageProps) {
  // Calculate centered position and scale to fit
  const imageProps = useMemo(() => {
    if (!image || !canvasWidth || !canvasHeight) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    // Use different scaling logic based on lab type
    let maxWidth;
    let maxHeight;

    if (labType === "transfers") {
      // Scale to 80% of canvas size for transfers lab
      maxWidth = canvasWidth * 0.8;
      maxHeight = canvasHeight * 0.8;
    } else {
      // Use padding-based scaling for design lab (default behavior)
      const padding = 40; // 40px padding on all sides
      maxWidth = canvasWidth - padding * 2;
      maxHeight = canvasHeight - padding * 2;
    }

    // Calculate scale to fit within canvas while maintaining aspect ratio
    const scaleX = maxWidth / image.width;
    const scaleY = maxHeight / image.height;
    const scale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond original size

    const width = image.width * scale;
    const height = image.height * scale;

    // Center the image
    const x = (canvasWidth - width) / 2;
    const y = (canvasHeight - height) / 2;

    return { x, y, width, height };
  }, [image, canvasWidth, canvasHeight, labType]);

  return (
    <Image
      image={image}
      x={imageProps.x}
      y={imageProps.y}
      width={imageProps.width}
      height={imageProps.height}
      opacity={1}
      listening={false}
    />
  );
}
